"use client";

import type { CSSProperties, HTMLAttributes } from "react";
import * as React from "react";
import { cn } from "../../lib";

/** Wrapper matching DaisyUI `<span class="countdown">`. */
export interface DaisyCountdownProps extends HTMLAttributes<HTMLSpanElement> {}

export function DaisyCountdown({
	className,
	children,
	...rest
}: DaisyCountdownProps) {
	return (
		<span className={cn("countdown", className)} {...rest}>
			{children}
		</span>
	);
}

export interface DaisyCountdownValueProps
	extends Omit<HTMLAttributes<HTMLOutputElement>, "children"> {
	/** Integer clamped to 0–999 (DaisyUI countdown range). */
	value: number;
	/** Minimum digit columns (`--digits`), same semantics as DaisyUI. */
	digits?: 1 | 2 | 3;
	children?: React.ReactNode;
}

/**
 * Inner digit strip: keep `--value` / optional `--digits` in sync with visible text.
 * @see https://daisyui.com/components/countdown/
 */
export function DaisyCountdownValue({
	value,
	digits,
	className,
	style,
	children,
	"aria-label": ariaLabel,
	...rest
}: DaisyCountdownValueProps) {
	const clamped = Math.min(999, Math.max(0, Math.round(value)));
	const label = String(clamped);

	const cssVars = {
		"--value": clamped,
		...(digits !== undefined ? { "--digits": digits } : {}),
	} as CSSProperties;

	return (
		<output
			className={cn(className)}
			style={{ ...cssVars, ...style }}
			aria-live="polite"
			aria-label={ariaLabel ?? label}
			{...rest}
		>
			{children ?? label}
		</output>
	);
}
